import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { createStyles, makeStyles } from '@material-ui/core';
import i18n from 'i18n-js';

import {
    getPulseDelayMs,
    getSkeletonHoldMs,
    SKELETON_FADE_MS,
    SKELETON_PULSE_MS,
    SKELETON_SHOW_DELAY_MS,
} from './skeletonTiming';

export const SKELETON_TOKENS = {
    block: '#EEEEEE',
    pulse: '#DEDEDE',
    panel: '#FFFFFF',
    sidePanel: '#F8F8F8',
    divider: '#E3E3E3',
    appBackground: 'linear-gradient(180deg, #ffd79a 9.9%, #ff9f9f 100%) fixed',
    radius: 4,
};

type Stage = 'cover' | 'skeleton' | 'fading' | 'done';

interface SkeletonBlockProps {
    height: number | string;
    width?: number | string;
    style?: React.CSSProperties;
}

interface SkeletonOverlayProps {
    isLoading: boolean;
    background?: string;
    style?: React.CSSProperties;
    children: React.ReactNode;
}

const useStyles = makeStyles(() =>
    createStyles({
        '@keyframes skeletonPulse': {
            '0%': { backgroundColor: SKELETON_TOKENS.block },
            '50%': { backgroundColor: SKELETON_TOKENS.pulse },
            '100%': { backgroundColor: SKELETON_TOKENS.block },
        },
        block: {
            flexShrink: 0,
            backgroundColor: SKELETON_TOKENS.block,
            borderRadius: SKELETON_TOKENS.radius,
            animation: `$skeletonPulse ${SKELETON_PULSE_MS}ms cubic-bezier(0.5, 0, 0.5, 1) infinite`,
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
            },
        },
        overlay: {
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: `opacity ${SKELETON_FADE_MS}ms`,
        },
        srOnly: {
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
        },
    })
);

function runAfter(ms: number, action: () => void) {
    const timer = setTimeout(action, ms);
    return () => clearTimeout(timer);
}

export function SkeletonBlock({ height, width = '100%', style }: SkeletonBlockProps) {
    const classes = useStyles();
    const [animationDelay] = useState(() => `${getPulseDelayMs(performance.now())}ms`);
    return <div className={classes.block} style={{ height, width, animationDelay, ...style }} />;
}

interface SkeletonTableProps {
    columnWidths: Array<number | undefined>;
    rowCount?: number;
    headerHeight?: number;
}

export function SkeletonTable({
    columnWidths,
    rowCount = 10,
    headerHeight = 56,
}: SkeletonTableProps) {
    const renderRow = (height: number, key: number) => (
        <div
            key={key}
            style={{ display: 'flex', alignItems: 'center', gap: 32, padding: '0 16px', height }}
        >
            {columnWidths.map((width, i) => (
                <SkeletonBlock
                    key={i}
                    height={24}
                    width={width}
                    style={width ? undefined : { flexShrink: 1 }}
                />
            ))}
        </div>
    );
    return (
        <>
            {renderRow(headerHeight, -1)}
            {Array.from({ length: rowCount }, (_, i) => renderRow(53, i))}
        </>
    );
}

export function SkeletonOverlay({
    isLoading,
    background = SKELETON_TOKENS.panel,
    style,
    children,
}: SkeletonOverlayProps) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [stage, setStage] = useState<Stage>(isLoading ? 'cover' : 'done');
    const overlayRef = useRef<HTMLDivElement>(null);
    const mountedAt = useRef(performance.now());
    const shownAt = useRef(0);
    const isActive = stage !== 'done';
    const isBlocking = stage === 'cover' || stage === 'skeleton';

    useEffect(() => {
        const overlay = overlayRef.current;
        if (!isBlocking || !overlay?.parentElement) return;
        const overlayTop = overlay.getBoundingClientRect().top;
        // A header above an offset overlay is not covered, so it stays usable.
        const covered = Array.from(overlay.parentElement.children).filter(
            (el) => el !== overlay && el.getBoundingClientRect().bottom > overlayTop
        );
        covered.forEach((el) => el.setAttribute('inert', ''));
        return () => covered.forEach((el) => el.removeAttribute('inert'));
    }, [isBlocking]);

    useEffect(() => {
        if (!isActive) return;
        dispatch({ type: 'GLOBAL_CHANGE_LOADING_ZONES', delta: 1 });
        return () => {
            dispatch({ type: 'GLOBAL_CHANGE_LOADING_ZONES', delta: -1 });
        };
    }, [isActive, dispatch]);

    useEffect(() => {
        if (stage === 'fading') return runAfter(SKELETON_FADE_MS, () => setStage('done'));
        if (stage === 'cover') {
            if (!isLoading) {
                setStage('done');
                return;
            }
            const elapsed = performance.now() - mountedAt.current;
            return runAfter(SKELETON_SHOW_DELAY_MS - elapsed, () => {
                shownAt.current = performance.now();
                setStage('skeleton');
            });
        }
        if (stage === 'skeleton' && !isLoading) {
            const shownFor = performance.now() - shownAt.current;
            return runAfter(getSkeletonHoldMs(shownFor), () => setStage('fading'));
        }
    }, [isLoading, stage]);

    if (stage === 'done') {
        return null;
    }

    const isFading = stage === 'fading';
    return (
        <div
            ref={overlayRef}
            className={classes.overlay}
            role="status"
            aria-busy={!isFading}
            style={{
                background,
                opacity: isFading ? 0 : 1,
                pointerEvents: isFading ? 'none' : 'auto',
                ...style,
            }}
        >
            <span className={classes.srOnly}>{i18n.t('common.loadingContent')}</span>
            {stage !== 'cover' && children}
        </div>
    );
}

const SKELETON_COLUMN: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 12 };
const SKELETON_ROW: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 16 };

interface TableSkeletonProps extends SkeletonTableProps {
    isLoading: boolean;
    style?: React.CSSProperties;
    /** Blocks for the toolbar row above the table. */
    children?: React.ReactNode;
}

export function TableSkeleton({ isLoading, style, children, ...table }: TableSkeletonProps) {
    return (
        <SkeletonOverlay isLoading={isLoading} style={style}>
            {children && (
                <div style={{ ...SKELETON_ROW, flexWrap: 'wrap', padding: 16 }}>{children}</div>
            )}
            <SkeletonTable {...table} />
        </SkeletonOverlay>
    );
}

function SkeletonFields({ count, style }: { count: number; style?: React.CSSProperties }) {
    return (
        <div style={{ ...SKELETON_COLUMN, flex: 1, ...style }}>
            {Array.from({ length: count }, (_, i) => (
                <React.Fragment key={i}>
                    <SkeletonBlock height={12} width={160} style={{ marginTop: 8 }} />
                    <SkeletonBlock height={48} />
                </React.Fragment>
            ))}
        </div>
    );
}

interface FormSkeletonProps {
    isLoading: boolean;
    /** Field count of each side-by-side column. */
    columns: number[];
    title?: boolean;
    actions?: number;
    /** Field count of the grey side panel on the right. */
    aside?: number;
    style?: React.CSSProperties;
}

export function FormSkeleton({
    isLoading,
    columns,
    title = false,
    actions = 0,
    aside = 0,
    style,
}: FormSkeletonProps) {
    return (
        <SkeletonOverlay isLoading={isLoading} style={{ flexDirection: 'row', ...style }}>
            <div style={{ ...SKELETON_COLUMN, flex: 1, gap: 20, padding: '24px 40px' }}>
                {title && <SkeletonBlock height={24} width={220} />}
                <div style={{ display: 'flex', gap: 40 }}>
                    {columns.map((count, i) => (
                        <SkeletonFields key={i} count={count} />
                    ))}
                </div>
                {actions > 0 && (
                    <div style={SKELETON_ROW}>
                        {Array.from({ length: actions }, (_, i) => (
                            <SkeletonBlock key={i} height={36} width={120} />
                        ))}
                    </div>
                )}
            </div>
            {aside > 0 && (
                <SkeletonFields
                    count={aside}
                    style={{
                        flex: '0 0 28%',
                        padding: 24,
                        backgroundColor: SKELETON_TOKENS.sidePanel,
                        borderLeft: `1px solid ${SKELETON_TOKENS.divider}`,
                    }}
                />
            )}
        </SkeletonOverlay>
    );
}
